import { Link } from "react-router-dom";

/**
 * Logo — monogramme + wordmark "Déstockage Luminaires".
 * Design inspiré du logo fourni (D stylisé cuivré dans un cadre rectangulaire),
 * revectorisé pour un rendu net à toutes les tailles et sur tous les fonds.
 *
 * Props:
 *  - variant : "dark" (défaut) pour fond clair (header cream) — texte ink
 *              "light" pour fond sombre (footer ink) — texte cream
 *  - size    : "sm" | "md" | "lg" — hauteur du monogramme
 *  - showTagline : booléen — affiche "La qualité à prix discount !" sous le wordmark
 *  - to      : lien (par défaut "/") ; passer null pour rendre non cliquable
 *  - className, testId
 */
const SIZE_MAP = {
  sm: { mono: "h-8 md:h-9", title: "text-[13px] md:text-sm", sub: "text-[10px] md:text-[11px]", tag: "text-[9px] md:text-[10px]" },
  md: { mono: "h-10 md:h-12", title: "text-[15px] md:text-lg", sub: "text-[11px] md:text-xs", tag: "text-[10px] md:text-[11px]" },
  lg: { mono: "h-14 md:h-16", title: "text-lg md:text-2xl", sub: "text-xs md:text-sm", tag: "text-[11px] md:text-xs" },
};

export const LogoMonogram = ({ className = "", primary = "#C9A36A", accent = "#9A6B3F" }) => (
  <svg
    viewBox="0 0 64 80"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    {/* Cadre vertical (référence : rectangle ouvert du logo original) */}
    <path
      d="M4 4 H60 V22 H14 V58 H60 V76 H4 Z"
      fill={primary}
    />
    {/* Arc du D — courbe interne cuivrée */}
    <path
      d="M22 18 H34 C46 18 54 26 54 40 C54 54 46 62 34 62 H22 V52 H32 C40 52 44 47 44 40 C44 33 40 28 32 28 H22 Z"
      fill={accent}
    />
  </svg>
);

const Wordmark = ({ variant = "dark", sizes, showTagline }) => {
  const textColor = variant === "light" ? "text-cream" : "text-ink";
  const subColor = variant === "light" ? "text-cream/70" : "text-graphite";
  return (
    <span className="flex flex-col leading-none">
      <span
        className={`font-sans font-semibold uppercase tracking-[0.14em] ${sizes.title} ${textColor}`}
      >
        Déstockage
      </span>
      <span
        className={`font-sans font-medium uppercase tracking-[0.32em] ${sizes.sub} ${subColor} mt-1`}
      >
        Luminaires
      </span>
      {showTagline && (
        <span
          className={`font-serif italic ${sizes.tag} text-gold/90 mt-1.5 tracking-wide`}
        >
          La qualité à prix discount
        </span>
      )}
    </span>
  );
};

export const Logo = ({
  variant = "dark",
  size = "md",
  showTagline = false,
  to = "/",
  className = "",
  testId = "logo-link",
  compact = false, // en mobile compact : n'affiche que le monogramme
}) => {
  const sizes = SIZE_MAP[size] ?? SIZE_MAP.md;
  const content = (
    <span className={`inline-flex items-center gap-2.5 md:gap-3 ${className}`}>
      <LogoMonogram className={`${sizes.mono} w-auto shrink-0`} />
      {/* Wordmark : caché sur très petit écran si compact, sinon toujours visible */}
      <span className={compact ? "hidden xs:inline-flex sm:inline-flex" : "inline-flex"}>
        <Wordmark variant={variant} sizes={sizes} showTagline={showTagline} />
      </span>
    </span>
  );

  if (!to) {
    return (
      <span data-testid={testId} className="select-none">
        {content}
      </span>
    );
  }

  return (
    <Link
      to={to}
      data-testid={testId}
      className="select-none inline-flex items-center hover:opacity-90 transition-opacity"
      aria-label="Déstockage Luminaires — accueil"
    >
      {content}
    </Link>
  );
};

export default Logo;
